import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import AppHeader from "@saleor/components/AppHeader";
import { CardSpacer } from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import { DateTime } from "@saleor/components/Date";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import Skeleton from "@saleor/components/Skeleton";
import { OrderDetailsListColumns } from "@saleor/config";
import { DropOffLocations_dropOffLocations_edges_node } from "@saleor/dropOff/types/DropOffLocations";
import useListSettings from "@saleor/hooks/useListSettings";
import useUser from "@saleor/hooks/useUser";
import { buttonMessages, sectionNames } from "@saleor/intl";
import { OrderUrlDialog } from "@saleor/motherOrders/urls";
import { isNationwideRTS } from "@saleor/motherOrders/utils/handlers";
import { ListProps, ListViews, UserPermissionProps } from "@saleor/types";
import {
  DeliveryTypeEnum,
  OrderVerticalType,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import { canManageFulfillment } from "@saleor/utils/handlers/userHandler";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe } from "../../../misc";
import {
  OrderDetails_order,
  OrderDetailsVariables
} from "../../types/OrderDetails";
import { orderImageIniData } from "../../utils/initialData";
import OrderAssignedRider from "../OrderAssignedRider";
import OrderCustomer from "../OrderCustomer";
import OrderDetailsList from "../OrderDetailsList";
import OrderHistory from "../OrderHistory";
import OrderInfo from "../OrderInfo";
import OrderParcelID from "../OrderParcelID/OrderParcelID";
import OrderPayment from "../OrderPayment/OrderPayment";
import OrderPrintShippingLabel from "../OrderPrintShippingLabel";
import OrderStatus from "../OrderStatus";

const useStyles = makeStyles(
  theme => ({
    cancelButton: {
      "& span": {
        color: "#E26139"
      },
      border: "1px solid #E26139",
      float: "right",
      marginTop: -50
    },
    date: {
      marginBottom: theme.spacing(3)
    },
    disabled: {
      opacity: "0.5",
      pointerEvents: "none"
    },
    header: {
      display: "flex",
      marginBottom: 0
    },
    headerHolder: {
      display: "inline-flex"
    },
    statusHolder: {
      display: "flex",
      marginLeft: "10px"
    }
  }),
  {
    name: "OrderDetailsPage"
  }
);

export interface OrderDetailsPageProps
  extends UserPermissionProps,
    ListProps<OrderDetailsListColumns> {
  dropOffList: DropOffLocations_dropOffLocations_edges_node[];
  onAssignHomeLocation: (id: string) => void;
  onSubmitMarkAsValid: (storeOrderId: any, parcelNo: any) => void;
  assignRiderDialog: any;
  order: OrderDetails_order;
  shippingMethods?: Array<{
    id: string;
    name: string;
  }>;
  countries?: Array<{
    code: string;
    label: string;
  }>;
  disabled: boolean;
  enableManageMallActions: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  motherOrderStatus?: string;
  onModal: (path: OrderUrlDialog) => void;
  ConfirmDialog: (data: SmopOrderStatusEnum) => void;
  ConfirmCompleted: (data: SmopOrderStatusEnum) => void;
  refetchOrder?: (variables?: OrderDetailsVariables) => Promise<any>;
  onBack();
  onFulfillmentCancel(id: string);
  onFulfillmentTrackingNumberUpdate(id: string);
  onProductClick?(id: string);
  onPaymentCapture();
  onPaymentPaid();
  onPaymentRefund();
  onPaymentVoid();
  onShippingAddressEdit();
  onOrderCancel();
  onProfileView();
  onInvoiceClick(invoiceId: string);
  onInvoiceSend(invoiceId: string);
  generateShippingDialog();
}

const OrderDetailsPage: React.FC<OrderDetailsPageProps> = props => {
  const {
    disabled,
    enableManageMallActions,
    order,
    saveButtonBarState,
    userPermissions,
    motherOrderStatus,
    onBack,
    onModal,
    ConfirmDialog,
    ConfirmCompleted,
    onPaymentCapture,
    onPaymentPaid,
    onPaymentRefund,
    onPaymentVoid,
    onShippingAddressEdit,
    onProfileView,
    onInvoiceClick,
    onInvoiceSend,
    assignRiderDialog,
    onSubmitMarkAsValid,
    generateShippingDialog,
    dropOffList,
    onAssignHomeLocation,
    refetchOrder,
    ...listProps
  } = props;
  const classes = useStyles(props);

  const intl = useIntl();
  const user = useUser();

  const isOpsAdmin = user.user?.userType.code === SmopUserTypeCode.OMI_OPS;

  const disabledRTSNationwide =
    disabled ||
    isNationwideRTS(order?.shippingMethod?.deliveryType, order?.smopStatus);

  const disableAction = !canManageFulfillment();

  const { settings } = useListSettings<OrderDetailsListColumns>(
    ListViews.ORDER_DETAILS_LIST
  );
  const handleCancelModal = () => {
    onModal("cancel");
  };

  const checkButton = () => {
    switch (order.smopStatus) {
      case SmopOrderStatusEnum.NEW_ORDER:
        return intl.formatMessage(buttonMessages.acknowledge);
      case SmopOrderStatusEnum.ACKNOWLEDGED:
        return intl.formatMessage(buttonMessages.ready);
      case SmopOrderStatusEnum.READY:
        return intl.formatMessage(buttonMessages.collect);
      case SmopOrderStatusEnum.COLLECTED:
        if (
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.SAME_DAY
        ) {
          return intl.formatMessage(buttonMessages.shipped);
        } else {
          return intl.formatMessage(buttonMessages.receivedAtFc);
        }
      case SmopOrderStatusEnum.RECEIVED_AT_FC:
        if (
          maybe(() => order?.verticalType) ===
            OrderVerticalType.FOOD_AND_BEVERAGES ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.SAME_DAY
        ) {
          return intl.formatMessage(buttonMessages.shipped);
        } else {
          return intl.formatMessage(buttonMessages.packed);
        }
      case SmopOrderStatusEnum.PACKED:
        return intl.formatMessage(buttonMessages.readyToShip);
      case SmopOrderStatusEnum.READY_TO_SHIP:
        return intl.formatMessage(buttonMessages.shipped);
      case SmopOrderStatusEnum.SHIPPED:
        return intl.formatMessage(buttonMessages.completed);
      default:
        return intl.formatMessage(buttonMessages.save);
    }
  };

  const checkAction = () => {
    switch (order.smopStatus) {
      case SmopOrderStatusEnum.COLLECTED:
        ConfirmDialog(order.smopStatus);
        break;
      case SmopOrderStatusEnum.RECEIVED_AT_FC:
        ConfirmDialog(order.smopStatus);
        break;
      case SmopOrderStatusEnum.PACKED:
        ConfirmDialog(order.smopStatus);
        break;
      case SmopOrderStatusEnum.READY_TO_SHIP:
        ConfirmDialog(order.smopStatus);
        break;
      case SmopOrderStatusEnum.SHIPPED:
        ConfirmCompleted(order.smopStatus);
        break;
      default:
        return intl.formatMessage(buttonMessages.save);
    }
  };

  const handleHideSave = () => {
    switch (order && order?.smopStatus) {
      case SmopOrderStatusEnum.RECEIVED_AT_FC:
        return false;
      case SmopOrderStatusEnum.PACKED:
        return false;
      case SmopOrderStatusEnum.READY_TO_SHIP:
        return false;
      case SmopOrderStatusEnum.SHIPPED:
        return false;
      case SmopOrderStatusEnum.COLLECTED:
        if (
          [
            DeliveryTypeEnum.EXPRESS,
            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE,
            DeliveryTypeEnum.SAME_DAY
          ].includes(maybe(() => order.shippingMethod.deliveryType))
        ) {
          return false;
        } else {
          return true;
        }
      default:
        return true;
    }
  };

  return (
    <Container>
      <AppHeader onBack={onBack}>
        {intl.formatMessage(sectionNames.fulfillment)}
      </AppHeader>
      <div
        id="cmpAdminFulfillmentMotherOrderDetailsPagePageHeader"
        className={classes.headerHolder}
      >
        <PageHeader
          className={classes.header}
          inline
          title={order && order?.orderCode ? order.orderCode : " "}
        />
        <span className={classes.statusHolder}>
          <OrderStatus
            status={order && order?.smopStatus ? order.smopStatus : " "}
          />
        </span>
      </div>
      <div id="txtAdminFulfillmentMotherOrderDetailsPageMallAssignment">
        {order?.mall?.name}
      </div>
      <div className={classes.date}>
        {order && order?.created ? (
          <Typography variant="caption">
            <DateTime date={order.created} />
          </Typography>
        ) : (
          <Skeleton style={{ width: "10em" }} />
        )}
      </div>
      <Grid>
        <div>
          {order && order?.subOrders ? (
            order.subOrders.map((orders, index) => (
              <OrderDetailsList
                onNextPage={undefined}
                onPreviousPage={undefined}
                onRowClick="undefined"
                onUpdateListSettings={undefined}
                pageInfo={undefined}
                key={index}
                order={orders}
                {...listProps}
                settings={settings}
                disabled={false}
                subOrderLength={order.subOrders.length}
                voucher={order.voucher}
                deliveryType={order?.shippingMethod?.deliveryType}
              />
            ))
          ) : (
            <Skeleton />
          )}
          <CardSpacer />

          <OrderPayment
            canManualAdjust={!disableAction}
            order={order}
            orderInfoComponent={
              <OrderInfo
                onCancel={handleCancelModal}
                order={order}
                dropOffList={dropOffList}
                onAssignHomeLocation={onAssignHomeLocation}
                canUpdateHomeLocation={!disableAction}
              />
            }
            refetchOrder={refetchOrder}
          />
          <CardSpacer />

          <OrderHistory
            events={maybe(() => order.events)}
            images={maybe(() => order.images, orderImageIniData)}
            dropOffImage={maybe(
              () => order?.thirdPartyRider?.deliveryCourier?.dropOffImageUrl,
              null
            )}
            pickUpImage={maybe(
              () => order?.thirdPartyRider?.deliveryCourier?.pickUpImageUrl,
              null
            )}
            deliveryType={maybe(() => order.shippingMethod.deliveryType)}
            returnImage={maybe(
              () => order?.thirdPartyRider?.deliveryCourier?.returnImageUrl
            )}
          />
        </div>
        <div>
          {/* Cancel button */}
          {(isOpsAdmin || enableManageMallActions) &&
            order &&
            order.smopStatus !== SmopOrderStatusEnum.CANCELED &&
            order.smopStatus !== SmopOrderStatusEnum.COMPLETED && (
              <Button
                id="btnAdminFulfillmentMotherOrderDetailsPageCancelButton"
                onClick={handleCancelModal}
                className={
                  disableAction
                    ? `${classes.cancelButton} ${classes.disabled}`
                    : classes.cancelButton
                }
              >
                <FormattedMessage
                  defaultMessage="Cancel Order"
                  description="button"
                />
              </Button>
            )}

          {maybe(() => order.shippingMethod.deliveryType) !==
          DeliveryTypeEnum.IN_STORE_PICKUP ? (
            <OrderAssignedRider
              riderData={order && order.rider}
              order={order}
              isThirdParty={maybe(() => order.isRider3pl)}
              thirdPartyData={maybe(() => order.thirdPartyRider)}
              status={order && order.smopStatus}
              assignedRiderDialog={assignRiderDialog}
              deliveryType={
                order &&
                order.shippingMethod &&
                order.shippingMethod.deliveryType
              }
              disableAction={disableAction}
            />
          ) : null}
          <CardSpacer />

          <OrderParcelID
            order={maybe(() => order)}
            onSubmitMarkAsValid={onSubmitMarkAsValid}
            disableAction={disableAction}
          />
          <CardSpacer />

          <OrderCustomer
            canEditAddresses={false}
            canEditCustomer={false}
            order={order}
            userPermissions={userPermissions}
            onShippingAddressEdit={onShippingAddressEdit}
            onProfileView={onProfileView}
          />

          {(order && order.smopStatus === "PACKED") ||
          (order && order.smopStatus === "READY_TO_SHIP") ||
          (order && order.smopStatus === "SHIPPED") ||
          (order && order.smopStatus === "COMPLETED") ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.SAME_DAY ||
          maybe(() => order.shippingMethod.deliveryType) ===
            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE ? (
            <>
              <CardSpacer />
              <OrderPrintShippingLabel
                order={order}
                generateDialog={generateShippingDialog}
                disableAction={disableAction}
              />
            </>
          ) : (
            ""
          )}
        </div>
      </Grid>

      <SaveButtonBar
        onCancel={onBack}
        onSave={checkAction}
        state={saveButtonBarState}
        order={order}
        disabled={handleHideSave() || disabledRTSNationwide}
        hideSave={handleHideSave()}
        labels={{ save: order && checkButton() }}
      />
    </Container>
  );
};
OrderDetailsPage.displayName = "OrderDetailsPage";
export default OrderDetailsPage;
